import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, ArrowRight, Save, Send, Sparkles, Loader2, Check, FileSignature,
  Image as ImageIcon, CalendarClock, ClipboardList, User, Info, Download, FlaskConical, RotateCcw,
} from "lucide-react";
import {
  getHorse, updateHorse, getPreview, generateWriteups, submitListing,
  getContractTemplate, signContract, contractPdfUrl, getSaleDates,
  mediaRawUrl, setMediaOrder, deleteSellerDraft,
} from "@/lib/api";
import { statusLabel, statusTone } from "@/lib/status";
import { Button } from "@/components/ui/button";
import { AppHeader } from "@/components/brand/AppHeader";
import { BrandShort, AuctionClose, closeShort } from "@/lib/brand";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import AdPreview from "@/components/AdPreview";
import { HeightSelect } from "@/components/adtemplate/AdFields";
import { fmtMT, todayCompany } from "@/lib/mountainTime";

const STEPS = [
  { id: 1, label: "Seller Info", icon: User },
  { id: 2, label: "Horse Info", icon: ClipboardList },
  { id: 3, label: "Sale Agreement", icon: FileSignature },
  { id: 4, label: "Media", icon: ImageIcon },
  { id: 5, label: "AI Advertisement", icon: Sparkles },
  { id: 6, label: "Sale Dates", icon: CalendarClock },
  { id: 7, label: "Review & Submit", icon: Check },
];

const SELLER_FIELDS = [
  ["customer_name", "Owner Name"], ["customer_phone", "Owner Phone"],
  ["horse_location_city", "Location City"], ["horse_location_state", "Province / State"],
  ["country", "Country"],
];
const HORSE_FIELDS = [
  ["horse_name", "Registered Name"], ["age", "Age"], ["year_born", "Year Born"],
  ["breed", "Breed"], ["gender", "Sex"], ["height_now", "Height"], ["color", "Color"],
  ["horse_type", "Discipline"], ["sire", "Sire"], ["dam", "Dam"],
  ["registration", "Registration #"], ["incentives", "Incentives"],
  ["trainer_name", "Trainer Name"], ["trainer_phone", "Trainer Phone"],
];
const REQUIRED = { horse_info: ["horse_name"], seller_info: ["customer_name", "horse_location_state"] };

function Fld({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="block text-[11px] uppercase tracking-wider text-stone-400 font-semibold mb-1.5">{label}</label>
      <Input data-testid={testid} value={value || ""} onChange={(e) => onChange(e.target.value)}
        className="border-stone-200 focus-visible:ring-amber-800" />
    </div>
  );
}

export default function SellerWizard() {
  const { draftId } = useParams();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [horse, setHorse] = useState(null);
  const [preview, setPreview] = useState(null);
  const [contract, setContract] = useState(null);
  const [saleDates, setSaleDates] = useState([]);
  const [busy, setBusy] = useState(false);
  const [gen, setGen] = useState(false);
  const [signName, setSignName] = useState("");
  const [consent, setConsent] = useState(false);

  const refreshPreview = useCallback(async () => {
    try { setPreview(await getPreview(draftId)); } catch { /* ignore */ }
  }, [draftId]);

  useEffect(() => {
    (async () => {
      try {
        const h = await getHorse(draftId);
        setHorse(h);
        setSignName(h.seller_info?.customer_name || "");
        const [ct, sd] = await Promise.all([getContractTemplate(draftId).catch((e) => ({ error: e?.response?.data?.detail || "The agreement could not be loaded." })), getSaleDates()]);
        setContract(ct);
        setSaleDates((sd.dates || []).filter((d) => d > todayCompany()));
        await refreshPreview();
      } catch {
        toast.error("Test draft not found");
        navigate("/seller-preview");
      }
    })();
  }, [draftId, navigate, refreshPreview]);

  const setField = (sec, k, v) => setHorse((h) => ({ ...h, [sec]: { ...(h[sec] || {}), [k]: v } }));
  const setWU = (k, v) => setHorse((h) => ({ ...h, write_up: { ...(h.write_up || {}), [k]: v } }));

  const persist = useCallback(async (extra = {}) => {
    const body = {
      horse_info: horse.horse_info, seller_info: horse.seller_info, sale_info: horse.sale_info,
      write_up_final: horse.write_up?.final_edited ?? undefined,
      selected_version: horse.write_up?.selected_version ?? undefined, ...extra,
    };
    await updateHorse(draftId, body);
  }, [horse, draftId]);

  const saveAnd = async (next) => {
    setBusy(true);
    try { await persist(); if (next != null) setStep(next); await refreshPreview(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
    finally { setBusy(false); }
  };

  const generate = async () => {
    setGen(true);
    try { await persist(); const wu = await generateWriteups(draftId); setHorse((h) => ({ ...h, write_up: { ...(h.write_up || {}), ...wu } })); toast.success("AI write-ups ready"); }
    catch (e) { toast.error(e.response?.data?.detail || "Generation failed"); }
    finally { setGen(false); }
  };

  const doSign = async () => {
    if (!signName.trim()) return toast.error("Enter the signer's name");
    if (!consent) return toast.error("Please agree to sign electronically");
    setBusy(true);
    try {
      await signContract(draftId, { signer_name: signName.trim(), signature_type: "typed", consent: true });
      setHorse(await getHorse(draftId));
      toast.success("Agreement signed (test)");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not sign"); }
    finally { setBusy(false); }
  };

  const numberMedia = async (attId, raw) => {
    const n = raw === "" ? null : parseInt(raw, 10);
    try {
      const media = await setMediaOrder(draftId, { [attId]: n });
      setHorse((h) => ({ ...h, media }));
    } catch { toast.error("Could not update order"); await refreshPreview(); }
  };

  const pickDate = async (d) => {
    setField("sale_info", "sale_close_date", d);
    setBusy(true);
    try { await updateHorse(draftId, { sale_info: { ...horse.sale_info, sale_close_date: d } }); await refreshPreview(); }
    finally { setBusy(false); }
  };

  const submit = async () => {
    setBusy(true);
    try { await persist(); const h = await submitListing(draftId); setHorse(h); toast.success("Submitted to admin review (test)"); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not submit"); }
    finally { setBusy(false); }
  };

  const reset = async () => {
    try { await deleteSellerDraft(draftId); toast.success("Test reset"); navigate("/seller-preview"); }
    catch { toast.error("Could not reset"); }
  };

  if (!horse) {
    return <div className="min-h-screen bg-[#FAF8F5] flex items-center justify-center"><Loader2 className="h-8 w-8 text-amber-800 animate-spin" /></div>;
  }

  const missing = [];
  for (const [sec, keys] of Object.entries(REQUIRED))
    for (const k of keys) if (!horse[sec]?.[k]) missing.push(k);
  const signed = horse.contract?.signed;
  const numbered = (horse.media || []).filter((m) => m.ad_order);

  return (
    <div className="min-h-screen bg-[#FAF8F5] pb-24 lg:pb-0 overflow-x-clip">
      <div data-testid="preview-mode-banner" className="bg-amber-800 text-white text-xs sm:text-sm font-medium px-4 py-2 flex items-center justify-between gap-2">
        <span className="flex items-center gap-1.5"><FlaskConical className="h-4 w-4" /> Seller Preview Mode — test only. Nothing here changes Smartsheet or live ads.</span>
        <Button data-testid="exit-preview-top" size="sm" variant="secondary" onClick={() => navigate("/")}
          className="rounded-full h-9 shrink-0 bg-white/15 hover:bg-white/25 text-white border-0">Exit Preview</Button>
      </div>
      <AppHeader title={horse.horse_info?.horse_name || "Untitled horse"} eyebrow="Seller Preview — Test Draft" onBack={() => navigate("/seller-preview")} backTestId="wizard-exit" width="max-w-6xl"
        below={
        <div className="bg-white border-b border-stone-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-2 overflow-x-auto">
          <div className="flex items-center gap-1 min-w-max" data-testid="wizard-progress">
            {STEPS.map((s, i) => {
              const Icon = s.icon;
              const done = s.id < step, active = s.id === step;
              return (
                <button key={s.id} data-testid={`step-tab-${s.id}`} onClick={() => setStep(s.id)}
                  className={`flex items-center gap-1.5 px-2.5 py-2 md:py-1 min-h-[40px] md:min-h-0 rounded-full text-xs font-medium transition-colors
                    ${active ? "bg-amber-800 text-white" : done ? "text-amber-800" : "text-stone-400"}`}>
                  <span className={`flex items-center justify-center h-5 w-5 rounded-full text-[10px]
                    ${active ? "bg-white/20" : done ? "bg-amber-100" : "bg-stone-100"}`}>
                    {done ? <Check className="h-3 w-3" /> : s.id}
                  </span>
                  <span className="hidden md:inline">{s.label}</span>
                  {i < STEPS.length - 1 && <span className="text-stone-400 mx-0.5">›</span>}
                </button>
              );
            })}
          </div>
        </div>
        </div>
        }>
          <Badge variant="outline" data-testid="wizard-status" className={`rounded-full shrink-0 ${statusTone(horse.listing_status)}`}>{statusLabel(horse.listing_status)}</Badge>
          <Button data-testid="wizard-reset" size="sm" variant="outline" onClick={reset}
            className="rounded-full border-stone-300 text-stone-600 hidden sm:inline-flex"><RotateCcw className="h-4 w-4 mr-1.5" /> Reset</Button>
      </AppHeader>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-5 md:py-6 grid lg:grid-cols-2 gap-6 min-w-0">
        <div className="space-y-5 min-w-0">
          {step === 1 && (
            <div className="grid sm:grid-cols-2 gap-4" data-testid="step-1">
              {SELLER_FIELDS.map(([k, l]) => <Fld key={k} label={l} testid={`sf-${k}`} value={horse.seller_info?.[k]} onChange={(v) => setField("seller_info", k, v)} />)}
            </div>
          )}
          {step === 2 && (
            <div className="grid sm:grid-cols-2 gap-4" data-testid="step-2">
              {HORSE_FIELDS.map(([k, l]) => k === "height_now" ? <HeightSelect key={k} label={l} testid={`hf-${k}`} value={horse.horse_info?.[k]} onChange={(v) => setField("horse_info", k, v)} /> : <Fld key={k} label={l} testid={`hf-${k}`} value={horse.horse_info?.[k]} onChange={(v) => setField("horse_info", k, v)} />)}
              <div className="sm:col-span-2">
                <label className="block text-[11px] uppercase tracking-wider text-stone-400 font-semibold mb-1.5">Description</label>
                <Textarea data-testid="hf-description" rows={4} value={horse.horse_info?.description || ""}
                  onChange={(e) => setField("horse_info", "description", e.target.value)} className="border-stone-200 focus-visible:ring-amber-800" />
              </div>
            </div>
          )}
          {step === 3 && (
            <div className="space-y-4" data-testid="step-3">
              {contract?.title && <p data-testid="contract-title" className="text-sm font-semibold text-stone-800">{contract.title} <span className="text-xs font-normal text-stone-500">· template {contract.version}</span></p>}
              {contract?.error && <p data-testid="contract-unavailable" className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3">{contract.error}</p>}
              <div className="rounded-xl border border-stone-200 bg-white p-4 max-h-72 overflow-y-auto whitespace-pre-wrap text-sm text-stone-700" data-testid="contract-text">
                {contract?.text || (contract?.error ? "" : "Loading agreement…")}
              </div>
              {signed ? (
                <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4" data-testid="contract-signed">
                  <p className="text-sm text-emerald-800 font-medium flex items-center gap-2"><Check className="h-4 w-4" /> Signed by {horse.contract.signer_name} (v{horse.contract.agreement_version})</p>
                  <p className="text-xs text-emerald-700 mt-1">On {fmtMT(horse.contract.signed_at)} · IP {horse.contract.ip || "—"}</p>
                  <a href={contractPdfUrl(draftId)} target="_blank" rel="noreferrer" data-testid="download-contract"
                    className="inline-flex items-center gap-1.5 text-sm text-amber-800 hover:underline mt-2"><Download className="h-4 w-4" /> Download signed PDF</a>
                  <div className="mt-2"><Button size="sm" variant="outline" data-testid="resign-button" onClick={() => setHorse((h) => ({ ...h, contract: {} }))} className="rounded-full">Sign again (new version)</Button></div>
                </div>
              ) : contract?.error ? null : (
                <div className="rounded-xl border border-stone-200 bg-white p-4 space-y-3">
                  <Fld label="Type your full legal name to sign" testid="sign-name" value={signName} onChange={setSignName} />
                  <label className="flex items-start gap-2 text-sm text-stone-600">
                    <Checkbox data-testid="sign-consent" checked={consent} onCheckedChange={(v) => setConsent(!!v)} className="mt-0.5" />
                    {contract?.consent_text || "I agree to sign this agreement electronically."} <span className="text-stone-400">(Preview — test copy only.)</span>
                  </label>
                  <Button data-testid="sign-button" onClick={doSign} disabled={busy}
                    className="rounded-full bg-amber-800 hover:bg-amber-900 text-white">
                    {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileSignature className="h-4 w-4 mr-2" />} Sign Agreement
                  </Button>
                </div>
              )}
            </div>
          )}
          {step === 4 && (
            <div className="space-y-3" data-testid="step-4">
              <p className="text-xs text-stone-500">Number only the media you want in the ad. Unnumbered items stay stored but unpublished. Numbers auto-renumber 1..n.</p>
              {(horse.media || []).length === 0 && <p className="text-sm text-stone-500">No media on this horse.</p>}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {(horse.media || []).map((m) => {
                  const isDoc = m.kind === "document";
                  return (
                    <div key={m.smartsheet_attachment_id} data-testid={`media-${m.smartsheet_attachment_id}`}
                      className={`rounded-xl border bg-white overflow-hidden ${m.ad_order ? "border-amber-400 ring-1 ring-amber-300" : "border-stone-200"}`}>
                      <div className="aspect-square bg-stone-100 flex items-center justify-center relative">
                        {m.kind === "image" ? (
                          <img src={mediaRawUrl(draftId, m.smartsheet_attachment_id)} alt={m.name} className="w-full h-full object-cover" />
                        ) : (
                          <span className="text-xs text-stone-500 px-2 text-center">{isDoc ? "📄 " : "🎬 "}{m.name}</span>
                        )}
                        {m.ad_order && <span className="absolute top-1 left-1 bg-amber-800 text-white text-xs font-bold rounded-full h-6 w-6 flex items-center justify-center">{m.ad_order}</span>}
                      </div>
                      {!isDoc && (
                        <div className="p-2">
                          <Input data-testid={`order-${m.smartsheet_attachment_id}`} inputMode="numeric"
                            defaultValue={m.ad_order || ""} placeholder="#"
                            onBlur={(e) => numberMedia(m.smartsheet_attachment_id, e.target.value.replace(/\D/g, ""))}
                            className="h-8 text-center border-stone-200 focus-visible:ring-amber-800" />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {step === 5 && (
            <div className="space-y-4" data-testid="step-5">
              <div>
                <label className="block text-[11px] uppercase tracking-wider text-stone-400 font-semibold mb-1.5">Your Original Write-Up</label>
                <Textarea data-testid="wu-original" rows={4} value={horse.write_up?.original || ""} onChange={(e) => setWU("original", e.target.value)}
                  className="border-stone-200 focus-visible:ring-amber-800 text-sm" />
              </div>
              <Button data-testid="generate-button" onClick={generate} disabled={gen} variant="outline"
                className="rounded-full border-amber-800 text-amber-800 hover:bg-amber-50">
                {gen ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />} Generate AI Versions
              </Button>
              {[1, 2, 3].map((n) => horse.write_up?.[`ai_version_${n}`] && (
                <div key={n} className={`rounded-xl border p-3 cursor-pointer ${String(horse.write_up?.selected_version) === String(n) ? "border-amber-400 ring-1 ring-amber-300 bg-amber-50/40" : "border-stone-200 bg-white"}`}
                  data-testid={`ai-version-${n}`} onClick={() => setWU("selected_version", n)}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[11px] uppercase tracking-wider text-amber-800 font-semibold">AI Version {n}</span>
                    {String(horse.write_up?.selected_version) === String(n) && <Badge className="bg-amber-800">Selected</Badge>}
                  </div>
                  <p className="text-sm text-stone-700 whitespace-pre-wrap">{horse.write_up[`ai_version_${n}`]}</p>
                </div>
              ))}
              <div>
                <label className="block text-[11px] uppercase tracking-wider text-stone-400 font-semibold mb-1.5">Final Write-Up (used in ad)</label>
                <Textarea data-testid="wu-final" rows={5} value={horse.write_up?.final_edited || ""} onChange={(e) => setWU("final_edited", e.target.value)}
                  className="border-stone-200 focus-visible:ring-amber-800 text-sm" />
              </div>
            </div>
          )}
          {step === 6 && (
            <div className="space-y-3" data-testid="step-6">
              <p className="text-sm text-stone-600">Pick a preferred sale date. All <BrandShort /> auctions close at <strong><AuctionClose /></strong>.</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {!saleDates.length && <p data-testid="no-upcoming-sale-dates" className="col-span-full text-sm text-stone-500">No upcoming sale dates are open yet — the team will schedule your sale.</p>}
                {saleDates.map((d) => (
                  <button key={d} data-testid={`sale-date-${d}`} onClick={() => pickDate(d)}
                    className={`rounded-xl border px-3 py-3 text-sm font-medium transition-colors
                      ${horse.sale_info?.sale_close_date === d ? "border-amber-500 bg-amber-50 text-amber-900" : "border-stone-200 bg-white text-stone-700 hover:border-amber-300"}`}>
                    {new Date(d + "T20:00:00").toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
                    <div className="text-[11px] text-stone-400">{closeShort()} close</div>
                  </button>
                ))}
              </div>
            </div>
          )}
          {step === 7 && (
            <div className="space-y-4" data-testid="step-7">
              {missing.length > 0 ? (
                <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900" data-testid="missing-info">
                  <p className="font-medium flex items-center gap-1.5"><Info className="h-4 w-4" /> Missing required information</p>
                  <ul className="list-disc ml-5 mt-1">{missing.map((m) => <li key={m}>{m.replace(/_/g, " ")}</li>)}</ul>
                </div>
              ) : (
                <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800 flex items-center gap-2" data-testid="all-complete">
                  <Check className="h-4 w-4" /> All required information is complete.
                </div>
              )}
              <ul className="text-sm text-stone-700 space-y-1.5 bg-white border border-stone-200 rounded-xl p-4" data-testid="review-checklist">
                <li>Agreement signed: <strong>{signed ? "Yes (v" + horse.contract.agreement_version + ")" : "No"}</strong></li>
                <li>Media selected for ad: <strong>{numbered.length}</strong></li>
                <li>Final write-up: <strong>{horse.write_up?.final_edited ? "Set" : "Not set"}</strong></li>
                <li>Sale close date: <strong>{horse.sale_info?.sale_close_date || "Not selected"}</strong></li>
              </ul>
              <div className="grid grid-cols-2 sm:flex sm:items-center gap-2 sm:gap-3">
                <Button data-testid="save-draft-button" variant="outline" onClick={() => saveAnd(null)} disabled={busy}
                  className="h-11 rounded-full border-stone-300 text-stone-700"><Save className="h-4 w-4 mr-2" /> Save Draft</Button>
                <Button data-testid="submit-review-button" onClick={submit} disabled={busy || horse.listing_status === "submitted"}
                  className="h-11 rounded-full bg-amber-800 hover:bg-amber-900 text-white"><Send className="h-4 w-4 mr-2" /> Submit for Review</Button>
              </div>
            </div>
          )}

          {/* Nav */}
          <div data-testid="wizard-nav" className="fixed lg:static bottom-0 inset-x-0 z-40 bg-white/95 lg:bg-transparent backdrop-blur-md lg:backdrop-blur-none border-t border-stone-200 px-4 py-3 lg:px-0 pb-[calc(0.75rem+env(safe-area-inset-bottom))] lg:pb-0 flex items-center justify-between gap-3">
            <Button data-testid="prev-step" variant="ghost" disabled={step === 1} onClick={() => setStep((s) => Math.max(1, s - 1))}
              className="h-11 text-stone-600"><ArrowLeft className="h-4 w-4 mr-1.5" /> Back</Button>
            {step < 7 && (
              <Button data-testid="next-step" onClick={() => saveAnd(step + 1)} disabled={busy}
                className="h-11 rounded-full bg-amber-800 hover:bg-amber-900 text-white">
                {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null} Save &amp; Next <ArrowRight className="h-4 w-4 ml-1.5" />
              </Button>
            )}
          </div>
        </div>

        <div className="lg:sticky lg:top-40 h-fit min-w-0">
          <div className="text-[11px] uppercase tracking-widest font-semibold text-amber-800 mb-2">Live Ad Preview</div>
          <AdPreview preview={preview} showVersions={false} />
        </div>
      </main>
    </div>
  );
}
